import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Grid from "material-ui/Grid";
import List, { ListItem } from "material-ui/List";
import ArrowDropDownIcon from 'material-ui-icons/ArrowDropDown';
import Divider from "material-ui/Divider";
import Switch from "material-ui/Switch";
import { FormControlLabel, FormGroup, FormControl } from "material-ui/Form";
import Typography from "material-ui/Typography";
import Input from "material-ui/Input";
import AnchoredMenu from "./AnchoredMenu";
import AutosuggestInputContainer from "./AutosuggestInputContainer";
import DatePickerDialogContainer from "./DatePickerDialogContainer";
import { withRouter } from "react-router-dom";
import { PERIOD_MAP } from "./actions/usage";
import {
  setQueryToPeriod,
  setQueryToEntity,
  setQueryToKeyValue,
} from "./helpers/overviewUrl";
import { localDateToUtcMoment } from "./helpers/dates";
 

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing.unit * 3,
    paddingBottom: theme.spacing.unit * 3,
    flexGrow: 1,
  },
  formControl: {
    margin: theme.spacing.unit,
    minWidth: 120,
  },
  list: {
    width: "100%",
  },
  listItem: {
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
  },
  divider: {
    margin: `${theme.spacing.unit * 3}px 0`,
  },
  noArrows: {
    // Hide HTML5 number input arrows:
    // Compatible with most modern browsers
    "& input[type=number]": {
      "-moz-appearance": "textfield",
      "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },
  borderInput: {
    "& input": {
      border: `1px solid ${theme.palette.primary.light}`,
      borderRadius: 4,
      padding: "10px 12px",
    },
  },
  inputLabel: {
    transform: "translate(14px, -6px) scale(0.75)",
    transformOrigin: "top left",
    backgroundColor: theme.palette.background.paper,
    paddingLeft: "2px",
    paddingRight: "2px",
  },
  formControlSpacing: {
    marginBottom: theme.spacing.unit * 4,

  },
  inputSpacing: {
    marginTop: theme.spacing.unit * 2,
    marginBottom: theme.spacing.unit, 
  },
  labelColorMatch: {
    color: "rgba(0, 0, 0, 0.87)",
  },
  filtersTitle: {
    fontWeight: 'bold', // Make the text bold
    paddingLeft: theme.spacing.unit * 2, // Inset about half as much as the other filters (assuming filters are indented by theme.spacing(2))
  },
  filterSection: {
    paddingBottom: theme.spacing.unit * 2,
    borderBottom: `1px solid  #ccc`
  }
});

/*
Each tab in the overview page will have a different set of filters. 
The mechanism below ensures the filters shown
are those that are applicable.
*/
const FILTERS_MAP = {
  time: ["period", "granularity", "account" ],
  accounts: ["period", "excludeZeroComputeToggle", "minStorageCostFilter"],
};

const OvFilterPanel = (props) => {
  /* This function is responsible for 
  changing the URL if the url filters will be valid with a new value - 
   - OR -
  simply setting the input value for autosuggest boxes
   */

  const {
    classes,
    openDatePicker,
    selectedPeriod,
    filterState,
    tabLabel,
    history,
    setAutosuggestInputValue,
    periodMenuItems,
    granularityMenuItems,
    selectedGranularity,
  } = props;

  const handleAutosuggestChange = (key, value) => {
    if (typeof value === "object" || !value) {
      history.push(setQueryToEntity(filterState, tabLabel, { key, value }));
    } else {
      setAutosuggestInputValue(key, value);
    }
  };

  const handlePeriodChange = (period) => {
    history.push(setQueryToPeriod(filterState, tabLabel, period));
  };

  const handleSelectPeriod = (value) => {
    if (value === "CUSTOM") {
      openDatePicker(selectedPeriod);
    } else {
      const dates = PERIOD_MAP[value].getDates();
      handlePeriodChange(dates);
    }
  };

  const handleExcludeZeroComputeChange = (event, checked) => {
    props.setExcludeZeroCompute(checked);
  };

  const handleStorageCostToggleChange = (event, checked) => {
    props.setMinStorageCostEnabled(checked);
  };

  const handleStorageCostInputChange = (event) => {
    const value = event.target.value;
    props.setMinStorageCost(value);
  };

  const handleDatePickerSave = (range) => {
    /* 
    range comes from the datepicker, and as such sets the dates in local time
    We need to make UTC moments from these with the same year month day
    */
    const { start, end } = range;
    const utcRange = {
      start: localDateToUtcMoment(start),
      end: localDateToUtcMoment(end),
    };
    handlePeriodChange(utcRange);
  };

  const handleSelectGranularity = (value) => {
    history.push(
      setQueryToKeyValue(filterState, tabLabel, "granularity", value)
    );
  };

  const filters = () => {
    return {
      period: (
        <ListItem key="period">
          <AnchoredMenu
            items={periodMenuItems}
            primary={[
                'Period (UTC) ', <ArrowDropDownIcon />
            ]}
            onSelect={handleSelectPeriod}
            secondary={`${selectedPeriod.start
              .utc()
              .format("Do MMM YY")} - ${selectedPeriod.end
              .utc()
              .format("Do MMM YY")}`}
          />
        </ListItem>
      ),
      granularity: (
        <AnchoredMenu
          key="granularity"
          items={granularityMenuItems}
          primary="Granularity"
          onSelect={handleSelectGranularity}
          secondary={selectedGranularity.label}
        />
      ),

      account: (
        <AutosuggestInputContainer
          key="account"
          placeholder="All accounts"
          label="Account"
          filterKey="account"
          onChange={handleAutosuggestChange}
        />
      ),
      excludeZeroComputeToggle: (
        <div className={classes.filterSection}>
  
        <FormControlLabel
          control={
            <Switch
              checked={filterState.excludeZeroCompute}
              onChange={handleExcludeZeroComputeChange}
            />
          }
          label="Remove Zero Compute"
          key="excludeZeroComputeToggle"
        />
        <Typography variant="body1">

        Only show accounts that have used compute resources regardless of whether they have incurred storage costs. Turn off to include accounts .
      </Typography>
          </ div>
 
      ),

      minStorageCostFilter: (
        <div className={classes.filterSection}>
        <FormGroup
          row
          key="minStorageCostFilter"
          className={classes.formControlSpacing}
        >
          <FormControlLabel
            control={
              <Switch
                checked={filterState.minStorageCostEnabled}
                onChange={handleStorageCostToggleChange}
              />
            }
            label="Min Storage Cost:"
            classes={{ label: classes.labelColorMatch }}
          />
          <FormControl
            className={`${classes.noArrows} ${classes.borderInput} ${classes.inputSpacing}`}
          >
            <Input
              id="min-storage-cost"
              defaultValue="1"
              type="text"
              onChange={handleStorageCostInputChange}
              disabled={!filterState.minStorageCostEnabled}
              margin="none"
              disableUnderline
            />
          </FormControl>
          <Typography variant="body1">
          Only show accounts whose storage cost for the period is above a threshold value. This filter is applied regardless of whether the account has compute resources or not.
      </Typography>
      
        </FormGroup>
        </div>
      )
    };
  };

  const renderFilters = () => {
    if (!(tabLabel in FILTERS_MAP)) {
      return [];
    }
    return FILTERS_MAP[tabLabel].map((filter) => filters()[filter]);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={16}>
        <Grid item xs={12}>
          <List className={classes.list}>

            <ListItem className={classes.listItem}>{filters().period}
            </ListItem>

            <Divider className={classes.divider} />

            <Typography
              variant="h6"
              className={classes.filtersTitle}
            >
              Additional Filters
            </Typography>


            {renderFilters()
              .filter((filter) => filter.key !== "period")
              .map((filter, index) => (
                <ListItem key={index} className={classes.listItem}>
                  {filter}
                </ListItem>
              ))}
          </List>

        </Grid>
      </Grid>
      <DatePickerDialogContainer onSave={handleDatePickerSave} />
    </div>
  );
};

OvFilterPanel.propTypes = {
  openDatePicker: PropTypes.func.isRequired,
  closeDatePicker: PropTypes.func.isRequired,
  periodMenuItems: PropTypes.array.isRequired,
  granularityMenuItems: PropTypes.array.isRequired,
  selectedPeriod: PropTypes.object.isRequired,
  selectedGranularity: PropTypes.object.isRequired,
  tabLabel: PropTypes.string.isRequired,
  setAutosuggestInputValue: PropTypes.func.isRequired,
  filterState: PropTypes.object.isRequired,
};

OvFilterPanel.defaultProps = {
  overviewDatePickerVisible: false,
};

export { OvFilterPanel };

export default withStyles(styles, { name: "OvFilterPanel" })(
  withRouter(OvFilterPanel)
);



// WEBPACK FOOTER //
// ./src/OvFilterPanel.js